import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import { useAuth } from '../../context/AuthContext';

export default function ClientsPage() {
  const { canEdit, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null); // null | 'create' | client obj
  const [form, setForm]         = useState({ name:'', industry:'', contact:'', notes:'' });
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState('');

  const load = () => api.getClients().then(setClients).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  function openCreate() { setForm({ name:'', industry:'', contact:'', notes:'' }); setModal('new'); }
  function openEdit(c)  { setForm({ name:c.name, industry:c.industry||'', contact:c.contact||'', notes:c.notes||'' }); setModal(c); }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (modal === 'new') {
        await api.createClient(form);
      } else {
        await api.updateClient(modal.id, form);
      }
      await load();
      setModal(null);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Archive this client? Their plans will be preserved.')) return;
    await api.deleteClient(id);
    load();
  }

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.industry||'').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{padding:'28px 32px'}}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <h1 style={styles.h1}>Clients</h1>
        {canEdit && <button style={styles.btnPrimary} onClick={openCreate}>+ New Client</button>}
      </div>

      <input
        placeholder="Search clients…" value={search} onChange={e => setSearch(e.target.value)}
        style={styles.search}
      />

      {loading ? <div style={styles.muted}>Loading…</div> : filtered.length === 0 ? (
        <div style={styles.empty}>
          {search ? 'No clients match your search.' : 'No clients yet. Create your first client.'}
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              {['Client', 'Industry', 'Contact', 'Plans', ''].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const total = Object.values(c.planCounts||{}).reduce((s,n)=>s+n,0);
              return (
                <tr key={c.id} style={styles.tr} onClick={() => navigate(`/clients/${c.id}`)}>
                  <td style={styles.td}>
                    <div style={{ fontWeight:700, color:'#0A0A0A' }}>{c.name}</div>
                  </td>
                  <td style={styles.td}><span style={styles.muted}>{c.industry||'—'}</span></td>
                  <td style={styles.td}><span style={styles.muted}>{c.contact||'—'}</span></td>
                  <td style={styles.td}>
                    <span style={{ color:'#C19A6B', fontWeight:600 }}>{total}</span>
                    {c.planCounts?.review > 0 && (
                      <span style={{ marginLeft:8, fontSize:11, color:'#B8860B' }}>
                        {c.planCounts.review} in review
                      </span>
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign:'right' }} onClick={e=>e.stopPropagation()}>
                    {canEdit && (
                      <button style={styles.btnGhost} onClick={() => openEdit(c)}>Edit</button>
                    )}
                    {isAdmin && (
                      <button style={{ ...styles.btnGhost, color:'#A02020', marginLeft:6 }}
                        onClick={() => handleDelete(c.id)}>Archive</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Modal */}
      {modal && (
        <Modal title={modal==='new' ? 'New Client' : `Edit: ${modal.name}`} onClose={() => setModal(null)}>
          {[
            { key:'name',     label:'Client Name *', placeholder:'e.g. Coca-Cola Moldova' },
            { key:'industry', label:'Industry',       placeholder:'e.g. FMCG, Telecom, Banking' },
            { key:'contact',  label:'Contact',        placeholder:'Contact person or email' },
          ].map(f => (
            <label key={f.key} style={{ display:'block', marginBottom:14 }}>
              <div style={styles.label}>{f.label}</div>
              <input
                value={form[f.key]} onChange={e => setForm(p=>({...p,[f.key]:e.target.value}))}
                placeholder={f.placeholder} style={styles.input}
              />
            </label>
          ))}
          <label style={{ display:'block', marginBottom:20 }}>
            <div style={styles.label}>Notes</div>
            <textarea value={form.notes} onChange={e => setForm(p=>({...p,notes:e.target.value}))}
              rows={3} style={{ ...styles.input, resize:'vertical' }} />
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={styles.btnGhost} onClick={() => setModal(null)}>Cancel</button>
            <button style={styles.btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : modal==='new' ? 'Create Client' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,.7)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000
    }} onClick={onClose}>
      <div style={{
        background:'#FFFFFF', border:'1px solid #E0E0E0', borderRadius:12,
        padding:28, width:480, maxWidth:'95vw', maxHeight:'90vh', overflow:'auto'
      }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:700, color:'#0A0A0A' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#9A8A7A', fontSize:20, cursor:'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const styles = {
  h1:        { margin:0, fontSize:22, fontWeight:800, color:'#0A0A0A' },
  muted:     { color:'#9A8A7A', fontSize:13 },
  search:    {
    width:'100%', boxSizing:'border-box', marginBottom:16,
    background:'#F5F5F5', border:'1px solid #E0E0E0', borderRadius:7,
    padding:'9px 14px', color:'#0A0A0A', fontSize:13, outline:'none'
  },
  table:     { width:'100%', borderCollapse:'collapse' },
  th:        { textAlign:'left', padding:'8px 12px', fontSize:11, fontWeight:600, color:'#9A8A7A', textTransform:'uppercase', letterSpacing:'.05em', borderBottom:'1px solid #21262D' },
  tr:        { cursor:'pointer', borderBottom:'1px solid #161B22' },
  td:        { padding:'12px 12px', fontSize:13, color:'#2A2A2A', background:'#F5F5F5' },
  btnPrimary:{ background:'#C19A6B', color:'#F5F5F5', border:'none', borderRadius:6, padding:'8px 14px', fontWeight:700, fontSize:13, cursor:'pointer' },
  btnGhost:  { background:'transparent', border:'1px solid #E0E0E0', borderRadius:6, padding:'6px 12px', color:'#9A8A7A', fontSize:12, cursor:'pointer' },
  label:     { fontSize:11, fontWeight:600, color:'#9A8A7A', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:5 },
  input:     { width:'100%', boxSizing:'border-box', background:'#F5F5F5', border:'1px solid #E0E0E0', borderRadius:6, padding:'9px 12px', color:'#0A0A0A', fontSize:13, outline:'none', fontFamily:"'Inter',sans-serif" },
  empty:     { background:'#FFFFFF', border:'1px solid #21262D', borderRadius:10, padding:32, textAlign:'center', color:'#9A8A7A', fontSize:14 },
};
